<template>
  <div>
    <div
      class="upload-holder"
      :class="{ block: isDragging, transparent: isTransparent }"
      @drop="drop"
      @dragover="dragover"
      @dragleave="dragleave"
    >
      <input
        type="file"
        :id="id"
        @change="onChange($event)"
        ref="file"
        @click="onInputClick"
        :accept="accept"
      />

      <!-- Progress Section -->
      <div class="w-full px-3 progress-container" v-if="isLoading">
        <div class="flex items-center justify-between pb-1.5">
          <p class="text-xs text-left font-bold">Uploading...</p>
          <p class="text-xs text-left font-bold">{{ progress }}%</p>
        </div>
        <div class="flex items-center justify-start w-full">
          <progress
            class="progressBar"
            :value="progress"
            max="100"
            style="width:100%;"
          ></progress>
        </div>
      </div>

      <label :for="id" v-else>
        <div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="7.5" stroke="black" />
            <path
              d="M8.35355 4.64645C8.15829 4.45118 7.84171 4.45118 7.64645 4.64645L4.46447 7.82843C4.2692 8.02369 4.2692 8.34027 4.46447 8.53553C4.65973 8.7308 4.97631 8.7308 5.17157 8.53553L8 5.70711L10.8284 8.53553C11.0237 8.7308 11.3403 8.7308 11.5355 8.53553C11.7308 8.34027 11.7308 8.02369 11.5355 7.82843L8.35355 4.64645ZM8.5 12L8.5 5L7.5 5L7.5 12L8.5 12Z"
              fill="black"
            />
          </svg>
          <p>
            Drag and drop file here or
            <span class="underline">choose file</span>
          </p>
        </div>
      </label>
    </div>

    <p class="accepted">Files supported: {{ fileSupport.join(', ') }}</p>
  </div>
</template>

<script>
import { mapState } from 'vuex'
export default {
  name: 'FileUpload',

  props: {
    id: {
      type: String,
      default: '',
      required: true
    },

    accept: {
      type: String,
      default: '',
      required: true
    },
    isTransparent: {
      type: Boolean,
      default: false,
      required: false
    },

    fileSupport: {
      type: Array,
      default: () => ['JPG', 'PNG', 'PDF', 'CSV']
    },

    loading: {
      type: Boolean,
      default: false
    },
    timeoutDuration: {
      type: Number,
      default: 1000
    }
  },

  watch: {
    allowListUploaded: {
      handler: function(val) {
        if (val) {
          this.filelist = []
        }
      },
      deep: true,
      immediate: true
    },
    loading: {
      handler: function(val) {
        if (val) {
          this.isLoading = true
          setTimeout(() => {
            this.progress = this.progress + Math.ceil(Math.random() * 10)
          }, this.timeoutDuration)
        } else {
          this.isLoading = false
        }
      },
      immediate: true
    },
    progress: {
      handler: function(val) {
        if (val >= 99) {
          this.isLoading = false
        } else {
          if (this.isLoading) {
            this.isLoading = true
            setTimeout(() => {
              this.progress = this.progress + Math.ceil(Math.random() * 10)
            }, this.timeoutDuration)
          }
        }
      },
      immediate: true
    }
  },

  computed: {
    ...mapState({
      allowListUploaded: state => state.groupOrder.allowListUploaded
    })
  },

  data: () => ({
    isDragging: false,
    filelist: [],
    progress: 0,
    isLoading: false
  }),

  methods: {
    onInputClick(e) {
      e.target.value = null
    },
    onChange(e) {
      const file = this.$refs.file.files[0]
      this.$emit('fileChange', e)
      this.filelist.push(file)
    },

    dragover(event) {
      event.preventDefault()
      this.isDragging = true
    },

    dragleave() {
      this.isDragging = false
    },

    drop(event) {
      console.log('drop', event)
      event.preventDefault()
      this.$refs.file.files = event.dataTransfer.files
      event.target['files'] = event.dataTransfer.files
      this.onChange(event) // Trigger the onChange event manually
      this.isDragging = false
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-holder {
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: #ffffff;
  box-sizing: border-box;
  transition: all 0.3s ease-in-out;
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%239B9B9BFF' stroke-width='2' stroke-dasharray='5%2c8' stroke-dashoffset='-3' stroke-linecap='square'/%3e%3c/svg%3e");

  &.transparent {
    background: transparent;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%239B9B9BFF' stroke-width='2' stroke-dasharray='5%2c8' stroke-dashoffset='-3' stroke-linecap='square'/%3e%3c/svg%3e");
  }
  &.block {
    transform: scale(1.06);
    margin: 10px;
  }

  input {
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
  }

  label {
    display: block;
    cursor: pointer;

    div {
      display: flex;
      align-items: center;

      p {
        color: #24292c;
        font-size: 0.813rem;
      }

      svg {
        margin-right: 8px;
      }
    }
  }

  section {
    width: 100%;
    display: flex;
    justify-content: left;
    align-self: start;
    flex-direction: column;
    gap: 10px;

    div {
      display: flex;
      align-items: center;

      p {
        font-size: 0.875rem;
        font-weight: bold;
        color: #24292c;
        text-align: left;
        word-wrap: break-word;
        width: 22%;
      }

      span {
        font-size: 0.875rem;
        cursor: pointer;
      }

      &:last-child {
        p {
          font-weight: 400;
          text-decoration: underline;
          cursor: pointer;
        }

        span {
          color: rgba(36, 41, 44, 0.5);
          font-size: 0.625rem;
        }
      }
    }
  }
}

[v-cloak] {
  display: none;
}

.progress-container {
  width: 70%;
  margin: 0 auto;
}

.accepted {
  color: rgba(36, 41, 44, 0.5);
  font-size: 0.625rem;
  padding-top: 8px;
  text-align: right;
}

progress {
  height: 6px;
}

progress::-webkit-progress-bar {
  background-color: #e6e6e6;
}

progress::-webkit-progress-value {
  background-color: #24292c;
}
</style>
